'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react'
import { refreshApplication } from './appUpdate'
import { evaluateUpdatePolicy, isDeploymentBuildId } from './policy'
import type { BuildVersionPayload } from './types'

const POLL_MS_PRODUCTION = 60 * 1000
const POLL_MS_NON_PRODUCTION = 5 * 60 * 1000

/** Per-tab: which remote build the user chose to postpone. */
const DISMISS_KEY = 'wlth.app-update.dismissed'
/** Per-device: which remote build we already force-refreshed for. Loop guard. */
const AUTO_REFRESH_KEY = 'wlth.app-update.auto-refreshed'

export const MANDATORY_COUNTDOWN_SECONDS = 10

export interface AppUpdateContextValue {
  updateAvailable: boolean
  /** True when the deployed build is flagged mandatory and we will refresh on our own. */
  mandatory: boolean
  /** Seconds left before an automatic refresh, or null when none is pending. */
  countdown: number | null
  localBuildId: string
  remote: BuildVersionPayload | null
  dismiss: () => void
  refresh: () => Promise<void>
}

const AppUpdateContext = createContext<AppUpdateContextValue | null>(null)

function readStorage(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value)
  } catch {
    // Private mode / quota. Losing the marker only costs an extra prompt.
  }
}

async function fetchRemoteBuild(): Promise<BuildVersionPayload | null> {
  try {
    const res = await fetch(`/build-version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) return null

    const data = (await res.json()) as Partial<BuildVersionPayload>
    const buildId = data.buildId?.trim()
    if (!buildId) return null

    return {
      buildId,
      builtAt: data.builtAt ?? '',
      version: data.version ?? '',
      mandatory: data.mandatory === true,
      title: data.title ?? null,
      highlights: Array.isArray(data.highlights)
        ? data.highlights
            .filter(
              (item): item is string =>
                typeof item === 'string' && item.trim().length > 0,
            )
            .slice(0, 3)
        : [],
    }
  } catch {
    // Offline or a transient blip. The next poll retries; never prompt on failure.
    return null
  }
}

export const AppUpdateProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const localBuildId = (process.env.COMMIT_HASH || '').trim()
  const enabled = isDeploymentBuildId(localBuildId)

  const [remote, setRemote] = useState<BuildVersionPayload | null>(null)
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [countdown, setCountdown] = useState<number | null>(null)
  const refreshingRef = useRef(false)

  const pollMs =
    process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
      ? POLL_MS_PRODUCTION
      : POLL_MS_NON_PRODUCTION

  const refresh = useCallback(async () => {
    if (refreshingRef.current) return
    refreshingRef.current = true
    await refreshApplication()
  }, [])

  const checkForUpdate = useCallback(async () => {
    if (!enabled) return

    const next = await fetchRemoteBuild()
    if (!next) return

    // The only staleness signal. Equal ids mean nothing happens — no prompt, no reload.
    if (next.buildId === localBuildId) {
      setUpdateAvailable(false)
      return
    }

    setRemote(next)

    const { updateAvailable: shouldPrompt } = evaluateUpdatePolicy({
      localBuildId,
      remoteBuildId: next.buildId,
      remoteMandatory: next.mandatory,
      dismissedBuildId: readStorage(sessionStorage, DISMISS_KEY),
      autoRefreshedBuildId: readStorage(localStorage, AUTO_REFRESH_KEY),
    })

    setUpdateAvailable(shouldPrompt)
  }, [enabled, localBuildId])

  useEffect(() => {
    if (!enabled) return

    void checkForUpdate()

    const interval = window.setInterval(() => {
      void checkForUpdate()
    }, pollMs)

    const onWake = () => {
      void checkForUpdate()
    }

    // `focus` and `online` are already edge-triggered and check unconditionally.
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      void checkForUpdate()
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('focus', onWake)
    window.addEventListener('online', onWake)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('focus', onWake)
      window.removeEventListener('online', onWake)
    }
  }, [checkForUpdate, enabled, pollMs])

  /**
   * Mandatory releases refresh on their own after a short countdown, so the user
   * sees why the page is about to reload instead of it happening under them.
   *
   * The `AUTO_REFRESH_KEY` guard is what stops this becoming a reload loop. If we
   * already force-refreshed for this build id and the client still reports as
   * stale — an edge-cached document, or a COMMIT_HASH mismatch between the bundle
   * and the server — refreshing again would not fix it. We degrade to the ordinary
   * dismissable banner instead of reloading forever.
   */
  const mandatory =
    updateAvailable &&
    evaluateUpdatePolicy({
      localBuildId,
      remoteBuildId: remote?.buildId ?? null,
      remoteMandatory: remote?.mandatory ?? false,
      dismissedBuildId: readStorage(sessionStorage, DISMISS_KEY),
      autoRefreshedBuildId: readStorage(localStorage, AUTO_REFRESH_KEY),
    }).mandatory

  useEffect(() => {
    if (!mandatory || !remote) {
      setCountdown(null)
      return
    }

    setCountdown(MANDATORY_COUNTDOWN_SECONDS)

    const timer = window.setInterval(() => {
      setCountdown((current) => (current === null ? null : Math.max(0, current - 1)))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [mandatory, remote])

  // Kept out of the tick updater above: a state updater must stay pure, and under
  // StrictMode it can run twice, which would fire the update twice.
  useEffect(() => {
    if (!mandatory || !remote || countdown !== 0) return

    // Recorded before the reload: this build gets exactly one automatic attempt.
    writeStorage(localStorage, AUTO_REFRESH_KEY, remote.buildId)
    void refresh()
  }, [countdown, mandatory, refresh, remote])

  const dismiss = useCallback(() => {
    if (remote) {
      writeStorage(sessionStorage, DISMISS_KEY, remote.buildId)
    }
    setUpdateAvailable(false)
  }, [remote])

  const value = useMemo<AppUpdateContextValue>(
    () => ({
      updateAvailable,
      mandatory,
      countdown,
      localBuildId,
      remote,
      dismiss,
      refresh,
    }),
    [updateAvailable, mandatory, countdown, localBuildId, remote, dismiss, refresh],
  )

  return <AppUpdateContext.Provider value={value}>{children}</AppUpdateContext.Provider>
}

export function useAppUpdate(): AppUpdateContextValue {
  const ctx = useContext(AppUpdateContext)
  if (!ctx) {
    throw new Error('useAppUpdate must be used within AppUpdateProvider')
  }
  return ctx
}
